import type { ClinicalCareEncounterModeEnum, ClinicalCareEncounterTypeEnum } from '@/shared/lib/types/clinicalCare';
import { create } from 'zustand';
import { DOCKED_PANEL_IDS, useDockedPanelStore } from './DockedPanelStore';

/** Everything the SOAP panel needs to render, snapshotted when it is opened. */
export type SoapPanelSession = {
  truentityId: string;
  encounterType: ClinicalCareEncounterTypeEnum;
  rpmFollowupConsultId?: string | null;
  clinicalCareEncounterId?: string | null;
  isReadOnly: boolean;
  isSignOffDisabled: boolean;
  isSignedOff: boolean;
  signedOffAt?: string | null;
  isAmendment: boolean;
  // Which sign-off documents the CCM flow generates. Carried on the session because the encounter
  // store is cleared when ClinicalCareEncounterSection unmounts, and sign-off can happen from a
  // docked panel on any route. Optional: only the CCM launcher sets it.
  encounterMode?: ClinicalCareEncounterModeEnum | null;
};

/** Which note a session points at. Used as the re-target check and as the dialog's remount key. */
export const soapSessionKey = (
  session: Pick<SoapPanelSession, 'encounterType' | 'rpmFollowupConsultId' | 'clinicalCareEncounterId'>
): string => `${session.encounterType}:${session.rpmFollowupConsultId ?? session.clinicalCareEncounterId ?? ''}`;

export type SoapPanelStore = {
  // Presence of a session is the open state; there is deliberately no separate `isOpen` flag.
  session: SoapPanelSession | null;
  openSoapPanel: (session: SoapPanelSession) => void;
  updateSoapPanel: (patch: Partial<SoapPanelSession>) => void;
  closeSoapPanel: () => void;
  // A swap is staged here so PatientDetails can save the outgoing draft before it takes effect.
  pendingSession: SoapPanelSession | null;
  requestSoapPanelSwap: (session: SoapPanelSession) => void;
  applyPendingSoapPanel: () => void;
  cancelPendingSoapPanel: () => void;
};

export const useSoapPanelStore = create<SoapPanelStore>((set, get) => ({
  session: null,
  pendingSession: null,
  openSoapPanel: session => {
    set(() => ({ session, pendingSession: null }));
    // Opening SOAP Notes while another docked panel is in front brings it forward.
    const docked = useDockedPanelStore.getState();
    if (docked.panels.some(panel => panel.id === DOCKED_PANEL_IDS.SOAP_NOTES)) docked.setActivePanel(DOCKED_PANEL_IDS.SOAP_NOTES);
  },
  // Only an already-open panel can be swapped — the FAB is the way in.
  requestSoapPanelSwap: session =>
    set(state => {
      if (!state.session) return {};
      if (soapSessionKey(state.session) === soapSessionKey(session)) return {};
      if (state.pendingSession && soapSessionKey(state.pendingSession) === soapSessionKey(session)) return {};
      return { pendingSession: session };
    }),
  applyPendingSoapPanel: () => {
    const { pendingSession } = get();
    if (!pendingSession) return;
    set(() => ({ session: pendingSession, pendingSession: null }));
  },
  cancelPendingSoapPanel: () => set(() => ({ pendingSession: null })),
  // No-ops when nothing changed; the launchers re-run this on every flag recalculation.
  updateSoapPanel: patch =>
    set(state => {
      if (!state.session) return {};
      const changed = Object.entries(patch).some(([key, value]) => state.session![key as keyof SoapPanelSession] !== value);
      return changed ? { session: { ...state.session, ...patch } } : {};
    }),
  /*
   * Resetting the dock keeps a stale `isDocked` from force-collapsing the nav forever -- but only
   * when SOAP Notes is the last panel at the edge. With Add Activity or an alert review stacked
   * beside it, a reset tore the whole dock down and took the other panel's place with it; then it
   * only leaves the stack.
   */
  closeSoapPanel: () => {
    const docked = useDockedPanelStore.getState();
    const hasOtherPanels = docked.panels.some(panel => panel.id !== DOCKED_PANEL_IDS.SOAP_NOTES);
    if (hasOtherPanels) docked.unregisterPanel(DOCKED_PANEL_IDS.SOAP_NOTES);
    else docked.resetDockState();
    set(() => ({ session: null, pendingSession: null }));
  }
}));
